/**
 * @fileoverview Component for launching a test on a device.
 * This component includes a confirmation message and a button for confirming the assignment.
 */

import { PlayCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import BackIcon from '../../../assets/back.svg'
import { useModal } from '../../../hooks/useModal'
import { logout } from '../../../utils/logout'
import { PrimaryButton } from '../../elements/PrimaryButton'
import { RegularButton } from '../../elements/RegularButton'
import { ShareAssignment } from './ShareAssignment'

/**
 * @interface LaunchTestProps
 * @description Props for the LaunchTest component.
 * @property {Novoic.API.AssignmentInvitation} invitation - The invitation object.
 * @property {string} participantId - The ID of the participant.
 * @property {boolean} isPhoneVerified - Whether the phone is verified.
 * @property {boolean} isEmailVerified - Whether the email is verified.
 */
interface LaunchTestProps {
    invitation: Novoic.API.AssignmentInvitation
    participantId: string
    isPhoneVerified: boolean
    isEmailVerified: boolean
}

/**
 * @function LaunchTest
 * @description Component for launching a test on a device.
 * @param {LaunchTestProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the confirmation message and button.
 */
export function LaunchTest({ invitation, ...rest }: LaunchTestProps) {
    const [open, close] = useModal()

    function onOpenLink() {
        window.open(invitation.link, '_blank')
        logout('security')
    }

    return (
        <div className="w-[416px] h-[436px]">
            <div className="flex justify-between items-center gap-4 mb-6">
                <div className="flex h-[28px]">
                    <BackIcon
                        onClick={() =>
                            open({
                                content: <ShareAssignment invitationId={invitation.id} {...rest} />,
                            })
                        }
                        className="cursor-pointer fill-gray-900"
                    />
                </div>
                <button className="w-6 h-6" onClick={close}>
                    <XMarkIcon />
                </button>
            </div>
            <div className="h-[386px] flex flex-col justify-between">
                <div>
                    <h1 className="text-gray-900 text-xl font-medium mb-4">
                        Launch test on this device?
                    </h1>
                    <p className="text-sm text-gray-500">
                        <b className="text-gray-900 font-medium">Important:</b> you will be logged
                        out after starting the test for security reasons. This is required to
                        prevent participants from accidentally accessing the score or any other
                        data.
                    </p>
                </div>
                <div className="flex gap-x-3 w-full items-center justify-center">
                    <RegularButton className="grow" onClick={() => close()}>
                        Cancel
                    </RegularButton>
                    <PrimaryButton className="grow" onClick={onOpenLink}>
                        <PlayCircleIcon className="w-5 h-5 mr-2" />
                        <span>Launch Test</span>
                    </PrimaryButton>
                </div>
            </div>
        </div>
    )
}
